import { useEffect, useState, type MouseEvent } from 'react';
import { Link } from 'react-router';

import { errorMessage } from '@/shared/api/errors';
import { useCan } from '@/shared/session/profiles';
import { Button } from '@/shared/ui/Button';
import { CheckIcon, FileTextIcon, PlusIcon } from '@/shared/ui/icons';
import { cn } from '@/shared/utils/cn';
import { money } from '@/shared/utils/money';

import {
  type BasketDraft,
  NEW_DRAFT,
  quantityFor,
  setChosenDraft,
  targetDraft,
  useAddToQuote,
  useBasket,
  useChosenDraft,
} from './basket';

/** How long «Añadido» stays on the button before it says «Añadir» again. */
const ADDED_MS = 2_500;

/** Inside a row that opens a dialog on click: the controls are not a click on the row.
 * Only the click: a key stopped here would never reach the dialog's Escape. */
function stopClick(event: MouseEvent): void {
  event.stopPropagation();
}

/**
 * The quantity and «Añadir» of one article of the tariffs (contract v10.52, ADR 0046 of
 * the back): the line goes to the end of the draft the bar above names, made first when
 * there is none. Only for a profile that makes quotes from the tariffs; nothing for the
 * rest. A replaced tariff answers 409 and the reason is said under the button.
 */
export function AddToQuote({
  itemId,
  reference,
  supplier,
  quotable = true,
  className,
}: {
  itemId: string;
  /** For the label of the quantity and what the screen reader hears after adding. */
  reference: string;
  /** Whose tariff, in the labels: one reference of two suppliers is two «Añadir». */
  supplier?: string | undefined;
  /** False for an article without a list price and a person who does not see the purchase
   * (contract v10.52): its price would give the cost away, and the back refuses it. */
  quotable?: boolean | undefined;
  className?: string;
}) {
  const can = useCan('tariff_quotes');
  const [typed, setTyped] = useState('1');
  const [added, setAdded] = useState(false);
  const add = useAddToQuote();
  const quantity = quantityFor(typed);

  useEffect(() => {
    if (!added) return undefined;
    const timer = window.setTimeout(() => setAdded(false), ADDED_MS);
    return () => window.clearTimeout(timer);
  }, [added]);

  if (!can) return null;
  if (!quotable) {
    return (
      <p className={cn('text-ink-3 max-w-56 text-[12.5px] leading-4', className)}>
        Sin PVP: su precio sale del de compra y lo presupuesta quien lo ve.
      </p>
    );
  }

  const whose = supplier ? ` (${supplier})` : '';

  function submit(): void {
    if (!quantity || add.isPending) return;
    // A second «Añadir» that fails must not leave the first one's «Añadido» over its error.
    setAdded(false);
    add.mutate([{ item_id: itemId, quantity }], { onSuccess: () => setAdded(true) });
  }

  return (
    <div className={cn('flex flex-col gap-1', className)} onClick={stopClick}>
      <div className="flex items-center gap-1.5">
        <input
          aria-label={`Cantidad de ${reference}${whose}`}
          className="field num h-8 w-16 px-1.5 text-[13.5px]"
          inputMode="decimal"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
          }}
        />
        <Button
          variant="secondary"
          size="sm"
          icon={added ? <CheckIcon size={15} /> : <PlusIcon size={15} />}
          onClick={submit}
          disabled={!quantity || add.isPending}
          aria-label={`Añadir ${reference} al presupuesto${whose}`}
        >
          {add.isPending ? 'Añadiendo…' : added ? 'Añadido' : 'Añadir'}
        </Button>
      </div>
      <span role="status" className="sr-only">
        {added ? `${reference} añadido al presupuesto` : ''}
      </span>
      {add.isError ? (
        <p role="alert" className="text-danger max-w-56 text-[12.5px] leading-4">
          {errorMessage(add.error)}
        </p>
      ) : null}
    </div>
  );
}

const shortDate = new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: '2-digit' });

function linesText(count: number): string {
  return `${count} ${count === 1 ? 'línea' : 'líneas'}`;
}

/** One draft as the picker names it: whose it is, how long it is, when it was begun and,
 * for whoever has both kinds, that it is the quote of a supplier's offer. */
function draftLabel(draft: BasketDraft): string {
  const who = draft.client_name ?? 'Sin cliente';
  const offer = draft.origin === 'offer' ? ' · de una oferta' : '';
  return `${who} · ${linesText(draft.lines_count)} · del ${shortDate.format(new Date(draft.created_at))}${offer}`;
}

/**
 * «Borrador: 7 líneas» (contract v10.52): which draft «Añadir» fills, the way to its
 * editor, and the choice of another one or of a new one. There is no cart apart from
 * the quote (the plan: «una pantalla más que aprender»), so this is all the basket is.
 */
export function BasketBar({ className }: { className?: string }) {
  const can = useCan('tariff_quotes');
  const basket = useBasket(can);
  const choice = useChosenDraft();
  if (!can || !basket.data) return null;

  const drafts = basket.data.drafts ?? [];
  const target = targetDraft(drafts, choice);

  return (
    <div
      role="region"
      aria-label="Presupuesto en curso"
      className={cn(
        'border-line bg-paper-2/60 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border px-3 py-2 text-[13.5px]',
        className,
      )}
    >
      <FileTextIcon size={16} className="text-ink-3 shrink-0" />
      {target ? (
        <span className="min-w-0">
          <Link to={`/presupuestos/${target.id}`} className="link font-medium">
            Borrador: {linesText(target.lines_count)}
          </Link>
          <span className="text-ink-3">
            {' '}
            · {target.client_name ?? 'sin cliente'} · {money(target.total_net)} sin IVA
          </span>
        </span>
      ) : (
        <span className="text-ink-2">Lo que añadas empieza un presupuesto nuevo.</span>
      )}
      {drafts.length > 0 ? (
        <label className="text-ink-3 flex max-w-full min-w-0 items-center gap-1.5 sm:ml-auto">
          <span className="shrink-0">Añadir a</span>
          <select
            className="field h-8 max-w-64 min-w-0 flex-1 py-0 text-[13px]"
            value={target?.id ?? NEW_DRAFT}
            onChange={(event) => setChosenDraft(event.target.value)}
          >
            {drafts.map((draft) => (
              <option key={draft.id} value={draft.id}>
                {draftLabel(draft)}
              </option>
            ))}
            <option value={NEW_DRAFT}>Un presupuesto nuevo</option>
          </select>
        </label>
      ) : null}
    </div>
  );
}
